import React, { useCallback, type CSSProperties } from 'react';
import { _ } from '@splunk/ui-utils/i18n';
import Button from '@splunk/react-ui/Button';
import { Modal } from '../Modal';
import { DEFAULT_ITEM_TYPE, modalHeaderText } from './constants';
import { ConfirmDeletionBody } from './ConfirmDeletionBody';
import type { ConfirmDeletionModalProps } from './types';

const modalStyle: CSSProperties = { width: '600px' };

export const ConfirmDeletionModal = ({
    itemId,
    itemName,
    itemType = DEFAULT_ITEM_TYPE,
    itemUsage,
    open,
    onClose,
    onRemove,
    returnFocus,
    dataTest = 'confirm-deletion-modal',
    ...modalProps
}: ConfirmDeletionModalProps) => {
    // if itemName is empty or undefined then use itemId. If that's also empty then fallback to "item"
    const nameOrId =
        (itemName?.trim() ? itemName : itemId) ?? DEFAULT_ITEM_TYPE;

    const handleConfirmDeletion = useCallback(() => {
        if (!itemId) {
            onClose();
            return;
        }

        onRemove({ id: itemId });
        onClose();
    }, [onRemove, onClose, itemId]);

    return (
        <Modal
            {...modalProps}
            onRequestClose={onClose}
            open={open}
            returnFocus={returnFocus}
            style={modalStyle}
            data-test={dataTest}
        >
            <Modal.Header title={modalHeaderText(nameOrId)} />
            <Modal.Body>
                <ConfirmDeletionBody
                    itemName={nameOrId}
                    // if itemType is empty or undefined then use "item"
                    itemType={itemType?.trim() ? itemType : DEFAULT_ITEM_TYPE}
                    itemUsage={itemUsage}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button
                    data-test="cancel-deletion-button"
                    appearance="secondary"
                    onClick={onClose}
                    label={_('Cancel')}
                />
                <Button
                    data-test="confirm-deletion-button"
                    appearance="primary"
                    onClick={handleConfirmDeletion}
                    label={_('Delete')}
                />
            </Modal.Footer>
        </Modal>
    );
};
